"use client";

import { useState } from "react";
import { env } from "@/lib/environment";
import { Typography, Section } from "@/components/ui";
import Image from "next/image";
import { NewButton } from "./NewButton";

const imgBaseUrl = `${env.NEXT_PUBLIC_CDN_URL}/images/home`;

const screenManagementFeatures = [
  {
    iconLight: imgBaseUrl + "/screen-management-drak.AxVrj_bnPxQQ.svg",
    iconDark: imgBaseUrl + "/screen-management-light.Qv0DCL5wmkZq.svg",
    image: imgBaseUrl + "/screen-managment.-KHbE54OhljB.webp",
    title: "Screen Management",
    description: "From 10 to 10,000 screens, add and control everything from one simple dashboard.",
  },
  {
    iconLight: imgBaseUrl + "/smart-scheduling-light.qeFPlyDj_tdT.svg",
    iconDark: imgBaseUrl + "/smart-scheduling-drak.TlkWsfcNj4i8.svg",
    image: imgBaseUrl + "/smart-scheduling.baVCNiZWy69W.webp",
    title: "Smart Scheduling",
    description:
      "Automate playlists and timing with Pickcel's smart scheduling for effortless screen management.",
  },
  {
    iconLight: imgBaseUrl + "/pickcel-go-light.L3ar_osf3iAw.svg",
    iconDark: imgBaseUrl + "/pickcel-go-drak.fl_1tDJaXJps.svg",
    image: imgBaseUrl + "/mobile-app.LU4a04Caktce.webp",
    title: "Pickcel Go (Mobile App)",
    description:
      "Update content, launch promos, and track screen health — all from your phone, wherever you are.",
  },
  {
    iconLight: imgBaseUrl + "/live-dashboard-light.BCj5OHTyO763.svg",
    iconDark: imgBaseUrl + "/live-dashboard-dark.WkoUtD3opWDH.svg",
    image: imgBaseUrl + "/live-dashaboard.CdQ20Sds_q9V.webp",
    title: "Live Dashboards & Streaming",
    description:
      "Stream real-time dashboards and live events straight to your screens — so teams act on insights instantly.",
  },
];

export function ScreenManagementFeatures() {
  const [selectedFeatureIndex, setSelectedFeatureIndex] = useState(3);
  const [prevSelectedFeatureIndex, setPrevSelectedFeatureIndex] = useState(3);
  const [isImageTransitioning, setIsImageTransitioning] = useState(false);

  const selectFeature = (index: number) => {
    if (index === selectedFeatureIndex) return;
    setIsImageTransitioning(true);
    setPrevSelectedFeatureIndex(selectedFeatureIndex);
    setSelectedFeatureIndex(index);
  };

  return (
    <Section variant="default" background="gray">
      <div className="flex text-center justify-center items-center flex-col gap-3 ">
        <span className="section-label">Features</span>
        <Typography variant="h2" as="h2" className="hidden md:block mx-auto max-w-3xl">
          Run Every Screen from One Powerful Dashboard
        </Typography>
        <Typography variant="h2" as="h2" className="w-[280px] block md:hidden mx-auto">
          Run Every
          <br /> Screen from One
          <br /> Powerful Dashboard
        </Typography>
        <Typography variant="body" as="p">
          One digital signage platform to add, schedule, and monitor thousands of screens — without
          extra hardware or IT.
        </Typography>
      </div>
      <div className="py-12 flex-row justify-between gap-12 items-center mx-auto w-full md:max-w-[1132px] hidden lg:flex">
        <div className="w-full lg:max-w-[468px] flex flex-col gap-1">
          {screenManagementFeatures.map((feature, index) => (
            <button
              key={index}
              type="button"
              onClick={() => selectFeature(index)}
              aria-pressed={selectedFeatureIndex === index}
              aria-label={`Select ${feature.title} feature`}
              className={`w-full text-left flex flex-row items-start gap-4 border rounded-lg p-6 cursor-pointer transition-all duration-300 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 ${
                selectedFeatureIndex === index ? "border-accent/40 bg-white" : "border-transparent"
              }`}
            >
              <Image
              src={selectedFeatureIndex === index ? feature.iconDark : feature.iconLight}
                alt={feature.title}
                title={feature.title}
                width={53}
                height={53}
                className="w-12 h-12 object-contain transition-all duration-300 ease-in-out"
              />
              <div className="flex flex-col gap-1.5">
                <Typography variant="h4" as="h3" className="font-semibold">
                  {feature.title}
                </Typography>
                <Typography variant="small" as="p">
                  {feature.description}
                </Typography>
              </div>
            </button>
          ))}
        </div>
        <div className="w-full lg:w-3/5 flex place-content-end">
          <div className="relative w-[605px] max-w-full aspect-4/3">
            <Image
              src={screenManagementFeatures[prevSelectedFeatureIndex].image}
              alt={screenManagementFeatures[prevSelectedFeatureIndex].title}
              title={screenManagementFeatures[prevSelectedFeatureIndex].title}
              className={`absolute inset-0 h-auto w-full transition-opacity duration-200 ease-out ${
                isImageTransitioning ? "opacity-100" : "opacity-0"
              }`}
              aria-hidden
              fill
              sizes="(min-width: 1024px) 605px, 100vw"
            />
            <Image
              src={screenManagementFeatures[selectedFeatureIndex].image}
              alt={screenManagementFeatures[selectedFeatureIndex].title}
              title={screenManagementFeatures[selectedFeatureIndex].title}
              className={`h-auto w-full transition-opacity duration-200 ease-out ${
                isImageTransitioning ? "opacity-0" : "opacity-100"
              }`}
              onLoadingComplete={() => {
                // Ensure we only fade in once the new image is ready.
                requestAnimationFrame(() => setIsImageTransitioning(false));
              }}
              fill
              sizes="(min-width: 1024px) 605px, 100vw"
            />
          </div>
        </div>
      </div>
      <div className="flex py-8 flex-col px-1.5 lg:hidden">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
          {screenManagementFeatures.map((feature, index) => (
            <div
              key={index}
              className="flex flex-col items-center bg-white gap-4 border border-[#DCDCDC] rounded-lg p-5 transition-all duration-200"
            >
              <Image
                src={feature.iconLight}
                alt={feature.title}
                title={feature.title}
                width={30}
                height={30}
                className="aspect-auto"
              />
              <div className="flex flex-col gap-2 text-center">
                <Typography variant="h3" as="h3" className="text-xl font-bold">
                  {feature.title}
                </Typography>
                <Typography variant="body" as="p" className="text-text-secondary text-center">
                  {feature.description}
                </Typography>
              </div>
              <Image
                src={feature.image}
                alt={feature.title}
                title={feature.title}
                width={250}
                height={173}
                className="w-full max-w-[250px] mx-auto"
              />
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-center">
        <a href="/digital-signage-software">
          <NewButton
            variant="primary"
            size="md"
            asChild          >
            Explore all features
          </NewButton>
        </a>
      </div>
    </Section>
  );
}
